import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Camera, X, CheckCircle, Warning, MapPin } from "@phosphor-icons/react";

export default function AddVisitPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cameraInputRef = useRef(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [locationStatus, setLocationStatus] = useState("idle"); // idle, loading, success, error

  useEffect(() => {
    fetchCustomer();
    requestLocation();
  }, [id]);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus("error");
      return;
    }
    
    setLocationStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setLocationStatus("success");
      },
      (error) => {
        console.error("GPS error:", error);
        setLocationStatus("error");
        toast.error("Could not get GPS location. Visit will be recorded without location.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${API}/customers/${id}`);
      setCustomer(response.data);
    } catch (error) {
      toast.error("Customer not found");
      navigate("/customers");
    } finally {
      setFetching(false);
    }
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please capture an image");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Image must be less than 10MB");
        return;
      }
      setPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target?.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
    }
  };

  const openCamera = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!photo) {
      toast.error("Please take a photo of the shop");
      return;
    }
    
    setLoading(true);

    try {
      // Create visit with GPS coordinates
      const visitResponse = await axios.post(`${API}/visits`, {
        customer_id: id,
        notes: notes || null,
        visit_date: new Date().toISOString(),
        latitude: location.latitude,
        longitude: location.longitude
      });

      // Upload photo
      const formData = new FormData();
      formData.append("file", photo);
      await axios.post(`${API}/visits/${visitResponse.data.id}/photo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Visit recorded successfully");
      navigate(`/customers/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record visit");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="add-visit-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="add-visit-page">
      {/* Header */}
      <div className="top-bar">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/customers/${id}`)}
            className="p-2 -ml-2 text-[#4B5563] hover:text-[#0A0A0A]"
            data-testid="back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <p className="overline">RECORD VISIT</p>
            <h1 className="top-bar-title">{customer?.name}</h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        {/* Camera Capture - Only allows taking photos, no file upload */}
        <div className="card-flat p-4">
          <Label className="text-[#0A0A0A] font-medium mb-1 block">
            Shop Verification Photo *
          </Label>
          <p className="text-sm text-[#4B5563] mb-3">
            Take a photo of the customer's shop for visit verification
          </p>

          {/* Hidden camera input - capture="environment" forces camera on mobile */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleCameraCapture}
            className="hidden"
            data-testid="camera-input"
          />

          {photoPreview ? (
            <div className="relative">
              <img
                src={photoPreview}
                alt="Visit preview"
                className="w-full h-56 object-cover rounded-lg"
                data-testid="photo-preview"
              />
              <button
                type="button"
                onClick={handleRemovePhoto}
                className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
                data-testid="remove-photo-button"
              >
                <X size={20} className="text-[#EF4444]" />
              </button>
              <div className="absolute bottom-2 left-2 flex items-center gap-1.5 px-3 py-1.5 bg-[#10B981] text-white rounded-full text-sm font-medium">
                <CheckCircle size={16} weight="fill" />
                Photo captured
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={openCamera}
              className="w-full border-2 border-dashed border-[#0055FF] rounded-xl p-8 flex flex-col items-center justify-center bg-[#F0F5FF] hover:bg-[#E0EBFF] transition-colors cursor-pointer"
              data-testid="take-photo-button"
            >
              <div className="w-16 h-16 rounded-full bg-[#0055FF] flex items-center justify-center mb-3">
                <Camera size={32} className="text-white" weight="fill" />
              </div>
              <p className="font-semibold text-[#0055FF] text-lg">Take Photo</p>
              <p className="text-sm text-[#4B5563] mt-1">Tap to open camera</p>
            </button>
          )}

          {/* Info notice */}
          <div className="flex items-start gap-2 mt-3 p-3 bg-[#FEF3C7] rounded-lg">
            <Warning size={20} className="text-[#D97706] flex-shrink-0 mt-0.5" weight="fill" />
            <p className="text-sm text-[#92400E]">
              Photo will be automatically stamped with date and time for verification.
            </p>
          </div>
          
          {/* GPS Status */}
          <div className={`flex items-center gap-2 mt-3 p-3 rounded-lg ${
            locationStatus === "success" ? "bg-[#D1FAE5]" : 
            locationStatus === "error" ? "bg-[#FEE2E2]" : 
            "bg-[#F3F4F6]"
          }`}>
            <MapPin size={20} className={`flex-shrink-0 ${
              locationStatus === "success" ? "text-[#059669]" : 
              locationStatus === "error" ? "text-[#DC2626]" : 
              "text-[#9CA3AF]"
            }`} weight="fill" />
            <p className={`text-sm ${
              locationStatus === "success" ? "text-[#065F46]" : 
              locationStatus === "error" ? "text-[#991B1B]" : 
              "text-[#4B5563]"
            }`}>
              {locationStatus === "loading" && "Getting GPS location..."}
              {locationStatus === "success" && "GPS location captured"}
              {locationStatus === "error" && "GPS unavailable - visit will be recorded without location"}
              {locationStatus === "idle" && "Waiting for GPS..."}
            </p>
            {locationStatus === "error" && (
              <button 
                type="button"
                onClick={requestLocation}
                className="text-sm text-[#0055FF] underline ml-auto"
              >
                Retry
              </button>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="card-flat p-4">
          <Label htmlFor="notes" className="text-[#0A0A0A] font-medium">
            Visit Notes (Optional)
          </Label>
          <Textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this visit..."
            rows={4}
            className="mt-1.5 border-[#E5E7EB] resize-none"
            data-testid="visit-notes-input"
          />
        </div>

        <Button
          type="submit"
          disabled={loading || !photo}
          className="w-full h-12 bg-[#0055FF] hover:bg-[#0040CC] text-white font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="submit-visit-button"
        >
          {loading ? "Recording..." : "Record Visit"}
        </Button>
      </form>
    </div>
  );
}
